import * as Sentry from "@sentry/react";
import { stop } from "ky/distribution/core/constants";
import routes from "routes/routes";
import { v4 as uuidv4 } from "uuid";

import { clearSalsaData } from "services/salsa";
import { EMagicLinkFlow } from "types/AuthFlowTypes";
import { CURRENT_ROLE_KEY, LOCALHOST_AUTH_TOKEN_KEY, SESSION_ID_KEY } from "constants/auth";
import { clearAiAssistantData } from "helpers/aiAssistant";
import { LOCAL_STORAGE_KEY as ACTIVITY_TRACKER_TIME_KEY } from "components/ActivityTracker/constants";
import { deleteIsDefaultCompanyBankAccountEnabledFromLocalStorage } from "components/CompanyBankAccountComponents/employee/EarlyPay";
import { removeFailedKycUserDataFromLocalStorage } from "components/CompanyBankAccountComponents/employee/KYCVerification/helpers";

import {
  mutationAuthControllerAuthorize,
  mutationAuthControllerAuthPasswordless,
  mutationAuthControllerAuthWithPassword,
  mutationAuthControllerGetGoogleOAuthUrl,
  mutationAuthControllerLogout,
  mutationUsersControllerUpdateUserActiveCompany,
  queryCompaniesControllerGetCompany,
  queryUsersControllerGetProfile,
} from "../utils/swagger_react_query";

export const setLocalhostAuthToken = (token: string) => {
  const isLocalhost = (window.location.origin || "").includes("http://localhost");
  if (isLocalhost) localStorage.setItem(LOCALHOST_AUTH_TOKEN_KEY, token);
};
export const getLocalhostAuthToken = () => localStorage.getItem(LOCALHOST_AUTH_TOKEN_KEY);
export const removeLocalhostAuthToken = () => localStorage.removeItem(LOCALHOST_AUTH_TOKEN_KEY);

export const setSessionId = () => localStorage.setItem(SESSION_ID_KEY, uuidv4());
export const getSessionId = () => localStorage.getItem(SESSION_ID_KEY);
export const removeSessionId = () => localStorage.removeItem(SESSION_ID_KEY);
export const removeActivityTracker = () => localStorage.removeItem(ACTIVITY_TRACKER_TIME_KEY);

export const sendMagicLink = async (email: string, mode: EMagicLinkFlow) => {
  await mutationAuthControllerAuthPasswordless()({ email, mode });
};

export const login = async (
  code: string,
  mode: EMagicLinkFlow,
  invitationId?: string,
  impersonateId?: string,
) => {
  removeFailedKycUserDataFromLocalStorage();
  deleteIsDefaultCompanyBankAccountEnabledFromLocalStorage();

  const response = await mutationAuthControllerAuthorize()({
    code,
    invitationId,
    mode,
    impersonateId,
  });
  setLocalhostAuthToken(response.token);

  setSessionId();

  return response;
};

export const loginWithPassword = async (email: string, password: string) => {
  removeFailedKycUserDataFromLocalStorage();
  deleteIsDefaultCompanyBankAccountEnabledFromLocalStorage();

  const response = await mutationAuthControllerAuthWithPassword()({
    email,
    password,
  });
  setLocalhostAuthToken(response.token);

  setSessionId();

  return response;
};

export const googleAuth = async (mode: EMagicLinkFlow) => {
  const response = await mutationAuthControllerGetGoogleOAuthUrl()({ mode });
  return response;
};

export const getUser = async () => {
  const response = await queryUsersControllerGetProfile();
  return response;
};

export const getCompany = async (companyId: string) => {
  if (!companyId) return null;
  const response = await queryCompaniesControllerGetCompany({ companyId });
  return response;
};

export const selectActiveCompany = async (companyId: string) => {
  const response = await mutationUsersControllerUpdateUserActiveCompany()({ companyId });
  clearAiAssistantData();
  return response;
};

export const cleanUp = () => {
  removeLocalhostAuthToken();
  removeSessionId();
  removeActivityTracker();
  localStorage.removeItem(CURRENT_ROLE_KEY);
  removeFailedKycUserDataFromLocalStorage();
  deleteIsDefaultCompanyBankAccountEnabledFromLocalStorage();
  clearAiAssistantData();
};

export const logout = async (
  withRequest: boolean = true,
  withNavigate: boolean = true,
  params?: string,
): Promise<void | typeof stop> => {
  try {
    if (withRequest) {
      await mutationAuthControllerLogout()();
    }

    cleanUp();
    clearSalsaData();

    Sentry.setUser(null);

    if (withNavigate) {
      window.location.href = routes.SIGN_IN;
    }
    if (params) window.location.href = routes.SIGN_IN + `?${params}`;
    return;
  } catch (err) {
    console.log(err);
    return;
  }
};
